import React, { useCallback, useRef } from 'react'
import { useSelector } from 'react-redux'
import {
    compName_character,
    livePortsNumber,
} from '../logic/panels/helpers/LiveButton'
import renderImage from '../../source/importImg'
import InputNumber from './InputNumber'
import './LiveRangeSliderControl.css'

// Module-level state to sync RGB channels across multiple components on the same hardware port
const sharedPortRgbState = {}

const CHANNEL_BADGE = { red: 'R', green: 'G', blue: 'B' }
const CHANNEL_COLOR = { red: '#e53935', green: '#43a047', blue: '#1e88e5' }

export default function LiveRangeSliderControl({
    comp,
    outputStates,
    onChange,
    highlightClass,
    style,
    isLiveStep,
}) {
    const worker      = useSelector((s) => s.worker)
    const webSerial   = useSelector((s) => s.webSerial)
    const isConnected = !!webSerial?.isConnected

    const p          = comp.properties || {}
    const startValue = Number(p.startValue ?? 0)
    const endValue   = Number(p.endValue   ?? 100)
    const range      = Math.abs(endValue - startValue)
    
    let step = 1
    if (p.stepSize != null) {
        step = Math.max(1, Math.round(Number(p.stepSize)))
    } else if (p.splits != null) {
        const splits = Math.max(1, Number(p.splits))
        step = range > 0 ? Math.max(1, Math.round(range / splits)) : 1
    }
    
    const minVal     = Math.min(startValue, endValue)
    const maxVal     = Math.max(startValue, endValue)

    const orientation = p.orientation || 'horizontal'
    const sliderType  = p.sliderType  || 'normal'
    const isVertical  = orientation === 'vertical'
    const isRgb       = sliderType === 'rgb'
    const label       = p.label || comp.name || ''

    const rgbChannels = p.rgbChannels?.length > 0 ? p.rgbChannels : ['red']
    const channelsToRender = isRgb ? rgbChannels : ['normal']

    const snapToStep = (v) => {
        const clamped = Math.min(maxVal, Math.max(minVal, v))
        if (step <= 0) return clamped
        const snapped = minVal + Math.round((clamped - minVal) / step) * step
        return Math.min(maxVal, Math.max(minVal, snapped))
    }

    const componentType = (comp.name || '').toLowerCase()
    const port          = comp.port

    const debounceTimer = useRef(null)

    const sendBytes = useCallback(
        (channel, value, currentOutputStates) => {
            if (!worker || !isConnected) return

            if (isRgb) {
                const portKey = port || 'default_rgb'
                // Read fresh values: use the just-changed value for `channel`,
                // and the shared port state for the other channels to avoid overwriting.
                const get = (ch) =>
                    ch === channel
                        ? value
                        : (sharedPortRgbState[portKey]?.[ch] ?? Number(currentOutputStates[`${comp.id}_${ch}`] ?? minVal))
                
                // Proportionally map [minVal, maxVal] → [0, 255] for each channel
                const rangeSpan = maxVal - minVal || 1
                const toByteVal = (v) => Math.max(0, Math.min(255, Math.round(((v - minVal) / rangeSpan) * 255)))
                const r = toByteVal(get('red'))
                const g = toByteVal(get('green'))
                const b = toByteVal(get('blue'))

                if (debounceTimer.current) clearTimeout(debounceTimer.current)
                debounceTimer.current = setTimeout(() => {
                    worker.postMessage({ type: 'writeArray', value: [76, 87, 76, 75, 0, r, g, b] })
                }, 30)
            } else {
                // Normal slider — skip during live-step auto-play (bytes are sent by PlayScreen)
                if (isLiveStep) return
                if (!port) return
                const device = sessionStorage.getItem('connectedDevice')
                if (!device) return
                const charCode = compName_character[componentType] || 'P'.charCodeAt(0)
                const portNum  = livePortsNumber?.[device]?.[port] ?? livePortsNumber?.Ace?.[port]
                if (portNum === undefined) return

                // Send raw value clamped to byte range — hardware uses its own scale (PWM 0-100, etc.)
                const byteVal = Math.max(0, Math.min(255, Math.round(value)))

                if (debounceTimer.current) clearTimeout(debounceTimer.current)
                debounceTimer.current = setTimeout(() => {
                    worker.postMessage({ type: 'writeArray', value: [76, 87, charCode, portNum, byteVal] })
                }, 30)
            }
        },
        [worker, isConnected, isLiveStep, isRgb, port, componentType, comp.id],
    )

    const handleChannelChange = (channel, v) => {
        const snapped = snapToStep(v)
        const mappingId = isRgb ? `${comp.id}_${channel}` : comp.id
        onChange(mappingId, snapped)
        
        if (isRgb) {
            const portKey = port || 'default_rgb'
            if (!sharedPortRgbState[portKey]) {
                sharedPortRgbState[portKey] = { red: minVal, green: minVal, blue: minVal }
            }
            sharedPortRgbState[portKey][channel] = snapped
        }

        // Pass the current outputStates snapshot so sendBytes can read sibling channels
        sendBytes(channel, snapped, outputStates)
    }


    const renderTrack = (channel) => {
        const mappingId = isRgb ? `${comp.id}_${channel}` : comp.id
        let rawVal = Number(outputStates[mappingId] ?? startValue)

        // Sync visually with other RGB components on the same port
        if (isRgb) {
            const portKey = port || 'default_rgb'
            if (sharedPortRgbState[portKey] && sharedPortRgbState[portKey][channel] !== undefined) {
                rawVal = sharedPortRgbState[portKey][channel]
            }
        }

        const val = snapToStep(rawVal)
        const displayVal = Math.round(val)

        const gradDir = isVertical ? 'to top' : 'to right'

        let trackStyle = {}
        if (isRgb) {
            // Vibrant channel gradient matching reference style
            const colors = {
                red:   ['#ffb3b3', '#d92100'],
                green: ['#b3e6b3', '#2e7d32'],
                blue:  ['#b3d9ff', '#1565c0'],
            }
            trackStyle = {
                backgroundImage: `linear-gradient(${gradDir}, ${colors[channel][0]} 0%, ${colors[channel][1]} 100%)`,
            }
        } else {
            trackStyle = {
                backgroundImage: (!p.rangImgName || p.rangImgName === 'frequency_slider') 
                                    ? `linear-gradient(${gradDir}, #FFE9C2 -8.7%, #FFBC45 100%)`
                                    : `url(${renderImage(p.rangImgName)})`,
                backgroundRepeat: 'no-repeat',
                backgroundSize: '100% 100%',
            }
        }
        // If no special style, CSS default gray gradient applies from .lrsc__range-bg

        const rowStyle = isVertical
            ? { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', flex: 1, minHeight: 0 }
            : { display: 'flex', alignItems: 'center', gap: '8px', marginTop: '2px' }

        const trackBgStyle = isVertical
            ? { ...trackStyle, width: '14px', height: '100%', borderRadius: '999px', position: 'relative' }
            : { ...trackStyle, width: '100%', height: '14px', borderRadius: '999px', position: 'relative' }

        return (
            <div key={channel} className="lrsc__row" style={rowStyle}>
                {isRgb && (
                    <span style={{ color: CHANNEL_COLOR[channel], fontWeight: 'bold', width: '12px', textAlign: 'center', fontSize: '14px' }}>
                        {CHANNEL_BADGE[channel]}
                    </span>
                )}
                
                {isVertical && <span style={{ fontSize: '13px', color: '#555', fontWeight: 'bold' }}>{maxVal}</span>}
                
                <div className="lrsc__track-cell" style={{ flex: '1 1 0', minHeight: 0, position: 'relative', display: 'flex', justifyContent: 'center', gap: '8px', width: isVertical ? 'auto' : '100%' }}>
                    {!isVertical && <span className="lrsc__min-label" style={{ fontSize: '13px', color: '#555', fontWeight: 'bold' }}>{minVal}</span>}
                    
                    <div className="lrsc__range-wrap" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', width: '100%' }}>
                        <div className="lrsc__range-bg" style={trackBgStyle}>
                            <input
                                type="range"
                                min={minVal}
                                max={maxVal}
                                step="any"
                                value={val}
                                className="lrsc__range-input"
                                onChange={(e) => handleChannelChange(channel, Number(e.target.value))}
                                style={isVertical
                                    ? { writingMode: 'vertical-lr', direction: 'rtl', width: '30px', height: '100%', margin: 0, position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)' }
                                    : { width: '100%', height: '14px', margin: 0 }}
                            />
                        </div>
                    </div>

                    {!isVertical && <span className="lrsc__max-label" style={{ fontSize: '13px', color: '#555', fontWeight: 'bold' }}>{maxVal}</span>}
                </div>
                
                {isVertical && <span style={{ fontSize: '13px', color: '#555', fontWeight: 'bold' }}>{minVal}</span>}
                
                <div className="lrsc__input-row" style={{ width: '46px', flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
                    <InputNumber
                        value={displayVal}
                        onChange={(v) => handleChannelChange(channel, v)}
                        min={minVal}
                        max={maxVal}
                    />
                </div>
            </div>
        )
    }

    return (
        <div
            id={comp.id}
            className={`absolute-component slider-wrapper lrsc lrsc--${isVertical ? 'v' : 'h'} ${highlightClass}`}
            style={{ ...style, zIndex: 20 }}
        >
            {label && (
                <div className="lrsc__header" style={{ marginBottom: '6px', textAlign: 'center' }}>
                    <span className="lrsc__name" style={{ fontSize: '14px', fontFamily: "'Halcyon_SemiBold', sans-serif" }}>{label}</span>
                </div>
            )}
            <div className="lrsc__slider-area" style={{ display: 'flex', flexDirection: isVertical ? 'row' : 'column', gap: '8px', flex: 1, minHeight: 0, justifyContent: 'center' }}>
                {channelsToRender.map(renderTrack)}
            </div>
        </div>
    )
}
